import * as XLSX from 'xlsx'

const safeNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0
  if (typeof value === 'number') return value
  return Number(String(value).replace(/,/g, '')) || 0
}

export const normalizeStatementData = (statement = {}) => {
  if (!statement) return {}
  if (statement.calculated_metrics) return statement.calculated_metrics
  if (statement.financial_data) return statement.financial_data
  if (statement.data) return statement.data
  return {}
}

export const getProfitLossMetrics = (data = {}) => {
  const products = Array.isArray(data.products) ? data.products : []
  const expenses = Array.isArray(data.operating_expenses) ? data.operating_expenses : []
  const totalRevenue = products.reduce((sum, product) => sum + safeNumber(product.revenue), 0)
  const totalCOGS = products.reduce((sum, product) => sum + safeNumber(product.cost_of_goods_sold), 0)
  const totalGrossProfit = totalRevenue - totalCOGS
  const totalOperatingExpenses = expenses.reduce((sum, expense) => sum + safeNumber(expense.amount), 0)
  const otherIncome = safeNumber(data.other_income)
  const otherExpenses = safeNumber(data.other_expenses)
  // Always recompute net income when the component lines are present, so
  // expenses are reflected even if a stale/manual net_income was stored.
  // Legacy flat statements (totals only) fall back to the stored figure.
  const computedNetIncome = totalGrossProfit - totalOperatingExpenses + otherIncome - otherExpenses
  const hasComponents = products.length > 0 || expenses.length > 0
  const netIncome = hasComponents ? computedNetIncome : (safeNumber(data.net_income) || computedNetIncome)

  return {
    products,
    expenses,
    totalRevenue,
    totalCOGS,
    totalGrossProfit,
    totalOperatingExpenses,
    otherIncome,
    otherExpenses,
    netIncome
  }
}

export const getBalanceSheetMetrics = (data = {}) => {
  const currentAssets = ['cash_and_equivalents', 'accounts_receivable', 'inventory', 'other_current_assets']
    .reduce((sum, key) => sum + safeNumber(data[key]), 0)
  const netFixedAssets = safeNumber(data.fixed_assets) - safeNumber(data.accumulated_depreciation)
  const totalAssets = currentAssets + netFixedAssets + safeNumber(data.other_assets)
  const currentLiabilities = ['accounts_payable', 'short_term_debt', 'other_current_liabilities']
    .reduce((sum, key) => sum + safeNumber(data[key]), 0)
  const totalLiabilities = currentLiabilities + safeNumber(data.long_term_debt) + safeNumber(data.other_liabilities)
  const totalEquity = safeNumber(data.owner_equity) + safeNumber(data.retained_earnings)
  const balanceDiff = totalAssets - (totalLiabilities + totalEquity)

  return {
    currentAssets,
    netFixedAssets,
    totalAssets,
    currentLiabilities,
    totalLiabilities,
    totalEquity,
    balanceDiff
  }
}

export const isBalanceSheetBalanced = (data = {}, tolerance = 1) => {
  const { balanceDiff } = getBalanceSheetMetrics(data)
  return Math.abs(balanceDiff) <= tolerance
}

const formatStatementType = (type) => {
  if (!type) return 'Unknown'
  const normalized = String(type).toLowerCase()
  if (normalized === 'profit_loss' || normalized === 'profit-loss' || normalized === 'profit loss') return 'Profit & Loss'
  if (normalized === 'balance_sheet' || normalized === 'balance-sheet' || normalized === 'balance sheet') return 'Balance Sheet'
  return String(type)
}

const buildCsvText = (rows) => {
  return rows.map((row) => row.map((cell) => {
    const value = cell === undefined || cell === null ? '' : String(cell)
    const escaped = value.replace(/"/g, '""')
    return `"${escaped}"`
  }).join(',')).join('\n')
}

const downloadBlob = (blob, filename) => {
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

export const buildStatementExportRows = (statement = {}) => {
  const data = normalizeStatementData(statement)
  const type = statement.statement_type || statement.statementType || statement.type || 'unknown'
  const rows = [
    ['Statement Type', formatStatementType(type)],
    ['Member', statement.customer_name || statement.customerName || statement.member_number || statement.memberNumber || statement.customer_id || statement.customerId || 'N/A'],
    ['Reporting Period', data.period_start && data.period_end ? `${data.period_start} to ${data.period_end}` : data.as_of_date || statement.reporting_period || 'N/A'],
    ['Status', statement.status || statement.status_label || 'N/A'],
    []
  ]

  if (type === 'profit_loss') {
    const metrics = getProfitLossMetrics(data)
    rows.push(['Products'])
    rows.push(['Product', 'Revenue', 'COGS', 'Gross Profit'])
    metrics.products.forEach((product) => {
      rows.push([
        product.product_name || product.productName || 'N/A',
        safeNumber(product.revenue),
        safeNumber(product.cost_of_goods_sold),
        safeNumber(product.gross_profit)
      ])
    })
    rows.push([])
    rows.push(['Operating Expenses'])
    rows.push(['Category', 'Description', 'Amount'])
    metrics.expenses.forEach((expense) => {
      rows.push([
        expense.category || expense.expense_category || 'N/A',
        expense.description || expense.expense_description || '',
        safeNumber(expense.amount)
      ])
    })
    rows.push([])
    rows.push(['Summary'])
    rows.push(['Total Revenue', metrics.totalRevenue])
    rows.push(['Total COGS', metrics.totalCOGS])
    rows.push(['Total Gross Profit', metrics.totalGrossProfit])
    rows.push(['Total Operating Expenses', metrics.totalOperatingExpenses])
    rows.push(['Other Income', metrics.otherIncome])
    rows.push(['Other Expenses', metrics.otherExpenses])
    rows.push(['Net Income', metrics.netIncome])
  } else if (type === 'balance_sheet') {
    const metrics = getBalanceSheetMetrics(data)
    rows.push(['Assets'])
    rows.push(['Current Assets', 'Amount'])
    rows.push(['Cash & Equivalents', safeNumber(data.cash_and_equivalents)])
    rows.push(['Accounts Receivable', safeNumber(data.accounts_receivable)])
    rows.push(['Inventory', safeNumber(data.inventory)])
    rows.push(['Other Current Assets', safeNumber(data.other_current_assets)])
    rows.push(['Total Current Assets', metrics.currentAssets])
    rows.push([])
    rows.push(['Non-Current Assets', 'Amount'])
    rows.push(['Fixed Assets', safeNumber(data.fixed_assets)])
    rows.push(['Accumulated Depreciation', safeNumber(data.accumulated_depreciation)])
    rows.push(['Net Fixed Assets', metrics.netFixedAssets])
    rows.push(['Other Assets', safeNumber(data.other_assets)])
    rows.push(['Total Assets', metrics.totalAssets])
    rows.push([])
    rows.push(['Liabilities', 'Amount'])
    rows.push(['Accounts Payable', safeNumber(data.accounts_payable)])
    rows.push(['Short-term Debt', safeNumber(data.short_term_debt)])
    rows.push(['Other Current Liabilities', safeNumber(data.other_current_liabilities)])
    rows.push(['Long-term Debt', safeNumber(data.long_term_debt)])
    rows.push(['Other Liabilities', safeNumber(data.other_liabilities)])
    rows.push(['Total Liabilities', metrics.totalLiabilities])
    rows.push([])
    rows.push(['Equity', 'Amount'])
    rows.push(['Owner Equity', safeNumber(data.owner_equity)])
    rows.push(['Retained Earnings', safeNumber(data.retained_earnings)])
    rows.push(['Total Equity', metrics.totalEquity])
    rows.push([])
    rows.push(['Balance Check', metrics.balanceDiff])
  } else {
    rows.push(['Financial Data'])
    Object.entries(data).forEach(([key, value]) => rows.push([key, value]))
  }

  return rows
}

export const exportStatementToExcel = (statement, filename = 'statement.xlsx') => {
  const rows = buildStatementExportRows(statement)
  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.aoa_to_sheet(rows)
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Statement')
  XLSX.writeFile(workbook, filename)
}

export const exportStatementToCsv = (statement, filename = 'statement.csv') => {
  const rows = buildStatementExportRows(statement)
  const csv = buildCsvText(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, filename)
}

export const exportStatementListToExcel = (statements = [], filename = 'statement_list.xlsx') => {
  const rows = [
    ['Statement ID', 'Type', 'Member', 'Reporting Period', 'Status', 'Total Revenue', 'Total Operating Expenses', 'Net Income', 'Total Assets', 'Total Liabilities', 'Total Equity', 'Balance Diff']
  ]

  statements.forEach((statement) => {
    const data = normalizeStatementData(statement)
    const type = statement.statement_type || statement.statementType || statement.type || 'unknown'
    const typeLabel = formatStatementType(type)
    const profitLoss = getProfitLossMetrics(data)
    const balanceSheet = getBalanceSheetMetrics(data)

    rows.push([
      statement.id || '',
      typeLabel,
      statement.customer_name || statement.customerName || statement.member_number || statement.memberNumber || '',
      data.period_start && data.period_end ? `${data.period_start} to ${data.period_end}` : data.as_of_date || statement.reporting_period || '',
      statement.status || '',
      profitLoss.totalRevenue || '',
      profitLoss.totalOperatingExpenses || '',
      profitLoss.netIncome || '',
      balanceSheet.totalAssets || '',
      balanceSheet.totalLiabilities || '',
      balanceSheet.totalEquity || '',
      balanceSheet.balanceDiff || ''
    ])
  })

  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.aoa_to_sheet(rows)
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Statements')
  XLSX.writeFile(workbook, filename)
}

// ── Cumulative report exports ─────────────────────────────────────────────────
// `report` is the `data` object returned by GET-CUMULATIVE-FINANCIAL-REPORT.
export const buildCumulativeReportRows = (report = {}, memberLabel = '') => {
  const pl = report.profit_loss || {}
  const totals = pl.totals || {}
  const bs = report.balance_sheet
  const rows = [
    ['Cumulative Financial Report'],
    ['Member', memberLabel || report.mem_no || 'N/A'],
    ['Period', `${report.start_date || 'Inception'} to ${report.end_date || 'To date'}`],
    ['P&L statements included', pl.statements_count || 0],
    []
  ]

  const products = pl.products || []
  if (products.length > 0) {
    rows.push(['Revenue by Product'])
    rows.push(['Product', 'Revenue', 'COGS', 'Gross Profit'])
    products.forEach((p) => {
      rows.push([p.product_name || 'Unnamed product', safeNumber(p.revenue), safeNumber(p.cost_of_goods_sold), safeNumber(p.gross_profit)])
    })
    rows.push([])
  }

  const expenses = pl.expenses || []
  if (expenses.length > 0) {
    rows.push(['Operating Expenses by Category'])
    rows.push(['Category', 'Amount'])
    expenses.forEach((e) => {
      rows.push([e.category || 'Uncategorised', safeNumber(e.amount)])
    })
    rows.push([])
  }

  rows.push(['Profit & Loss Summary (Cumulative)'])
  rows.push(['Total Revenue', safeNumber(totals.total_revenue)])
  rows.push(['Total COGS', safeNumber(totals.total_cogs)])
  rows.push(['Gross Profit', safeNumber(totals.gross_profit)])
  rows.push(['Total Operating Expenses', safeNumber(totals.total_operating_expenses)])
  rows.push(['Other Income', safeNumber(totals.other_income)])
  rows.push(['Other Expenses', safeNumber(totals.other_expenses)])
  rows.push(['Net Profit', safeNumber(totals.net_income)])

  if (bs && bs.totals) {
    rows.push([])
    rows.push([`Balance Sheet as at ${bs.as_of || report.end_date || 'latest'}`])
    rows.push(['Total Current Assets', safeNumber(bs.totals.total_current_assets)])
    rows.push(['Net Fixed Assets', safeNumber(bs.totals.net_fixed_assets)])
    rows.push(['Total Assets', safeNumber(bs.totals.total_assets)])
    rows.push(['Total Current Liabilities', safeNumber(bs.totals.total_current_liabilities)])
    rows.push(['Total Liabilities', safeNumber(bs.totals.total_liabilities)])
    rows.push(['Total Equity', safeNumber(bs.totals.total_equity)])
    rows.push(['Balance Check', safeNumber(bs.totals.balance_diff)])
  }

  return rows
}

export const exportCumulativeReportToExcel = (report, memberLabel = '', filename = 'cumulative_report.xlsx') => {
  const rows = buildCumulativeReportRows(report, memberLabel)
  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.aoa_to_sheet(rows)
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Cumulative Report')
  XLSX.writeFile(workbook, filename)
}

export const exportCumulativeReportToCsv = (report, memberLabel = '', filename = 'cumulative_report.csv') => {
  const rows = buildCumulativeReportRows(report, memberLabel)
  const csv = buildCsvText(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, filename)
}

export const exportStatementListToCsv = (statements = [], filename = 'statement_list.csv') => {
  const rows = [
    ['Statement ID', 'Type', 'Member', 'Reporting Period', 'Status', 'Total Revenue', 'Total Operating Expenses', 'Net Income', 'Total Assets', 'Total Liabilities', 'Total Equity', 'Balance Diff']
  ]

  statements.forEach((statement) => {
    const data = normalizeStatementData(statement)
    const type = statement.statement_type || statement.statementType || statement.type || 'unknown'
    const typeLabel = formatStatementType(type)
    const profitLoss = getProfitLossMetrics(data)
    const balanceSheet = getBalanceSheetMetrics(data)

    rows.push([
      statement.id || '',
      typeLabel,
      statement.customer_name || statement.customerName || statement.member_number || statement.memberNumber || '',
      data.period_start && data.period_end ? `${data.period_start} to ${data.period_end}` : data.as_of_date || statement.reporting_period || '',
      statement.status || '',
      profitLoss.totalRevenue || '',
      profitLoss.totalOperatingExpenses || '',
      profitLoss.netIncome || '',
      balanceSheet.totalAssets || '',
      balanceSheet.totalLiabilities || '',
      balanceSheet.totalEquity || '',
      balanceSheet.balanceDiff || ''
    ])
  })

  const csv = buildCsvText(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, filename)
}
